// src/app/core/mainPage/prompt/prompt-examples/components/PromptExamplesBlock.tsx
import { motion } from "motion/react";
import type { PromptExamplesBlockProps } from "../types";
import { FeaturedPromptCard } from "./FeaturedPromptCard";
import { GalleryPromptCard } from "./GalleryPromptCard";
import { TextPromptCard } from "./TextPromptCard";
import { PromptExamplesSkeleton } from "../states/PromptExamplesSkeleton";
import { useEffect, useState } from "react";

export function PromptExamplesBlock({
  isLoading = false,
  featuredExamples,
  galleryExamples,
  textExamples,
  copiedId,
  onCopy,
  onSelect,
  className,   // новый пропс
}: PromptExamplesBlockProps & { className?: string }) {
  const [columnCount, setColumnCount] = useState(4);

  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 640) setColumnCount(2);
      else if (width < 1024) setColumnCount(3);
      else if (width < 1400) setColumnCount(4);
      else setColumnCount(5);
    };
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  if (isLoading) {
    return <PromptExamplesSkeleton />;
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 8, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.25 } },
  };

  return (
    <>
      {/* Featured (крупные) карточки */}
      <motion.div className="grid gap-4 md:grid-cols-2 mb-6">
        {featuredExamples.map((example) => (
          <motion.div key={example.id} variants={itemVariants}>
            <FeaturedPromptCard
              example={example}
              copied={copiedId === example.id}
              onCopy={onCopy}
              onSelect={onSelect}
            />
          </motion.div>
        ))}
      </motion.div>

      {/* Галерея – компактная masonry */}
      <div
        className="mb-6"
        style={{
          columnCount: columnCount,
          columnGap: '8px',
        }}
      >
        {galleryExamples.map((example) => (
          <motion.div
            key={example.id}
            variants={itemVariants}
            style={{ breakInside: 'avoid', marginBottom: '8px' }}
          >
            <GalleryPromptCard example={example} onSelect={onSelect} />
          </motion.div>
        ))}
      </div>

      {/* Текстовые примеры – можно передать className для отступа */}
      <div className={className ?? 'mb-6'}>
        <div
          className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {textExamples.map((example) => (
            <motion.div
              key={example.id}
              variants={itemVariants}
              style={{ scrollSnapAlign: 'start' }}
            >
              <TextPromptCard
                example={example}
                copied={copiedId === example.id}
                onCopy={onCopy}
                onSelect={onSelect}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </>
  );
}